import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";

import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import {
  IAPIState,
  IStatus,
  IInfoBlockItem,
  IPlace,
  IContact,
  IFile,
  IImageItem,
  IInfoBlockItemType,
} from "shared/types";
import { getInfoBlockItemReadableName } from "shared/utils";

export interface ChooseInfoBlockItemBaseProps {
  type: IInfoBlockItem;
  Component: React.ComponentType<any>;
  componentProps?: { [key: string]: any };
  handleClose: () => void;
  handleAddNew: () => void;
  patchInfoBlock?: (item: IInfoBlockItemType, type: IInfoBlockItem) => void;
  infoBlockApiStatus?: IAPIState;
  hasError?: boolean; // info block patch error
  disabledItems?: IInfoBlockItemType[];
}

interface ChooseInfoBlockItemHookProps {
  type: IInfoBlockItem;
  items: (IPlace | IContact | IFile | IImageItem)[];
  handleClose: () => void;
  handleAddNew: () => void;
  infoBlockApiStatus?: IAPIState;
  getItemsApiStatus?: IAPIState;
}

export const useChooseInfoBlockItem = ({
  type,
  handleClose,
  handleAddNew,
  infoBlockApiStatus,
  getItemsApiStatus,
  items,
}: ChooseInfoBlockItemHookProps) => {
  const dispatch = useDispatch();
  const itemReadbleName = useMemo(
    () => getInfoBlockItemReadableName(type),
    [type]
  );

  const fetchedOnce = useRef<boolean>(false);
  useEffect(() => {
    if (getItemsApiStatus === IAPIState.succeeded) {
      fetchedOnce.current = true;
    }
  }, [getItemsApiStatus]);

  useEffect(() => {
    if (
      getItemsApiStatus === IAPIState.succeeded &&
      (!items || items.length === 0)
    ) {
      handleClose();
      handleAddNew();
    }
  }, [getItemsApiStatus, items, handleAddNew, handleClose]);

  useEffect(() => {
    if (infoBlockApiStatus === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          message: copy.chooseInfoBlockItem.success.replace(
            "%s",
            itemReadbleName
          ),
          status: IStatus.success,
        })
      );

      handleClose();
    }
  }, [infoBlockApiStatus, dispatch, itemReadbleName, handleClose]);

  const openCreateModal = () => {
    handleClose();
    handleAddNew();
  };

  return { fetchedOnce, openCreateModal, itemReadbleName };
};
